import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../api";
import { useAuth } from "../context/AuthContext";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { getCached, setCached } from "../lib/dataCache";
import { buildAvailableItems, filterVendorsByMarketplaceItems, itemMatchesMarketplaceFilters, parseMarketplaceSearch, SUPPORTED_MARKETPLACE_CITIES, vendorMatchesLocation, vendorMatchesQuery } from "../lib/homeDiscovery";
import {
  Star, MapPin, ChefHat, ArrowRight, Search, Store, Truck, Sparkles,
  Package, Utensils, ShieldCheck, Heart,
} from "lucide-react";
import AbandonedOrderBanner from "../components/AbandonedOrderBanner";
import { publicPickupArea } from "@/constants/compliance";
import HeroFoodCarousel from "../components/HeroFoodCarousel";
import FollowKitchenButton from "../components/FollowKitchenButton.jsx";
import MarketplaceFilterBar from "../components/MarketplaceFilterBar.jsx";
import MarketplaceStorySections from "../components/MarketplaceStorySections.jsx";
import { useBusinessStatus } from "../hooks/useBusinessStatus";

const CACHE_VENDORS = "home:vendors";
const CACHE_PREVIEWS = "home:menuCatalog";

const HERO_DESSERT_IMAGE = "/customer-final/bread-pudding.jpg";
const MARKETPLACE_HERO_VIDEO = "/customer-final/marketplace-hero.mp4";

export function homeFoodFallbackImage(category, images = {}) {
  const label = String(category || "").trim();
  if (images?.[label]) return images[label];
  const normalized = label.toLowerCase();
  if (/drink|beverage|juice|lemonade/.test(normalized)) return "/approved-food/drinks.jpg";
  if (/fresh produce|produce|fruit|vegetable/.test(normalized)) return "/approved-food/fresh-produce.jpg";
  if (/side|fries|chips/.test(normalized)) return "/approved-food/sides.jpg";
  if (/taco|mexican/.test(normalized)) return "/approved-food/tacos.jpeg";
  if (/burger|sandwich/.test(normalized)) return "/approved-food/burger.jpeg";
  if (/bak|bread|pastr/.test(normalized)) return "/approved-food/bakery.jpeg";
  if (/dessert|sweet|cake/.test(normalized)) return "/approved-food/dessert.jpeg";
  if (/bowl|breakfast|meal prep|hot meal|prepared|entree|main/.test(normalized)) return "/approved-food/bowls.jpeg";
  return "";
}

function ownerManagedMarketingImage(value, fallback) {
  const src = String(value || "");
  if (src.startsWith("/api/cms-media/") || src.startsWith("/customer-final/") || src.startsWith("/customer-hero/")) return src;
  return fallback;
}

function ownerMarketingText(value, fallback, legacyDefaults = []) {
  const text = String(value || "").trim();
  if (!text || legacyDefaults.includes(text)) return fallback;
  return text;
}
const HIDE_CUISINE_PATTERNS = [/test/i, /phase/i, /audit/i, /qa\b/i, /internal/i, /demo/i, /placeholder/i];

export default function Home() {
  const { user } = useAuth();
  const businessStatus = useBusinessStatus();
  const [searchParams, setSearchParams] = useSearchParams();
  const [vendors, setVendors] = useState(() => getCached(CACHE_VENDORS) ?? null);
  const [menuPreviews, setMenuPreviews] = useState(() => getCached(CACHE_PREVIEWS) ?? {});
  const [menuPreviewsReady, setMenuPreviewsReady] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [query, setQuery] = useState("");
  const [heroSearchText, setHeroSearchText] = useState("");
  const [locationCity, setLocationCity] = useState("");
  const [showCityPicker, setShowCityPicker] = useState(false);
  const [showAllFood, setShowAllFood] = useState(false);
  useEffect(() => { if (showAllFood) document.getElementById("full-food-catalog")?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [showAllFood]);
  useEffect(() => { if (user?.role === "customer" && SUPPORTED_MARKETPLACE_CITIES.includes(user.city)) setLocationCity((city) => city || user.city); }, [user?.id, user?.city, user?.role]);
  const [cuisine, setCuisine] = useState("");
  const [foodCategory, setFoodCategory] = useState("");
  const [fulfillmentFilter, setFulfillmentFilter] = useState("");
  const [dietaryFilter, setDietaryFilter] = useState("");
  const [priceFilter, setPriceFilter] = useState("");
  const [announcements, setAnnouncements] = useState([]);
  const [publicDrops, setPublicDrops] = useState([]);
  const [followedVendorIds, setFollowedVendorIds] = useState([]);
  const { settings, previewMode } = useSiteSettings();
  const home = settings?.homepage || {};
  const marketplace = settings?.marketplace || {};
  const heroBackdropImage = ownerManagedMarketingImage(home.hero_background_image, HERO_DESSERT_IMAGE);
  const heroBadgeText = ownerMarketingText(home.badge_text, "Alaska local food marketplace", ["Alaska's Local Food Marketplace"]);
  const heroHeadline = ownerMarketingText(home.headline, "Order local food from", ["Local food."]);
  const heroHeadlineEmphasis = ownerMarketingText(home.headline_emphasis, "Alaska kitchens.", ["Closer to home."]);
  const heroSubheadline = ownerMarketingText(
    home.subheadline,
    "Home cooks, bakers, food trucks, caterers, and local kitchens. Find pickup, available delivery, and limited preorder drops in one place.",
    ["Find meals, desserts, baked goods, comfort food, meal prep, catering, and specialty foods from independent Alaska kitchens."],
  );
  const heroSearchPlaceholder = ownerMarketingText(
    home.search_placeholder,
    "Search food, cuisines, or kitchens",
    ["Tacos, cake, comfort food, sourdough…"],
  );
  const showGuestMarketing = !user || previewMode;

  useEffect(() => {
    let cancelled = false;
    if (user?.role !== "customer" || previewMode) {
      setFollowedVendorIds([]);
      return () => { cancelled = true; };
    }
    void api.get("/retention/follows/me")
      .then(({ data }) => {
        if (cancelled) return;
        const ids = (Array.isArray(data) ? data : [])
          .map((row) => row?.vendor?.id || row?.vendor_id)
          .filter(Boolean);
        setFollowedVendorIds(ids);
      })
      .catch(() => {
        if (!cancelled) setFollowedVendorIds([]);
      });
    return () => { cancelled = true; };
  }, [user?.id, user?.role, previewMode]);

  useEffect(() => {
    if (searchParams.get("deactivated") === "1") {
      toast.success("Your account has been deactivated. You've been signed out.", { duration: 6000 });
      const next = new URLSearchParams(searchParams);
      next.delete("deactivated");
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadLegacyCatalog = async () => {
      // Rollback-compatible fallback only. Normal production uses the batched
      // /marketplace/catalog endpoint and never takes this N+1 path.
      const vendorResponse = await api.get("/vendors");
      const nextVendors = Array.isArray(vendorResponse.data) ? vendorResponse.data : [];
      const results = await Promise.all(nextVendors.map(async (vendor) => {
        try {
          const menuResponse = await api.get(`/vendors/${vendor.id}/menu`);
          return [vendor.id, Array.isArray(menuResponse.data) ? menuResponse.data : []];
        } catch {
          return [vendor.id, []];
        }
      }));
      return {
        vendors: nextVendors,
        menus: Object.fromEntries(results),
      };
    };

    const loadCatalog = async () => {
      try {
        let payload;
        try {
          const { data } = await api.get("/marketplace/catalog");
          payload = data;
        } catch (error) {
          if (error?.response?.status !== 404) throw error;
          payload = await loadLegacyCatalog();
        }

        if (cancelled) return;
        const nextVendors = Array.isArray(payload?.vendors) ? payload.vendors : [];
        const nextMenus = payload?.menus && typeof payload.menus === "object"
          ? payload.menus
          : {};

        setVendors(nextVendors);
        setMenuPreviews(nextMenus);
        setCached(CACHE_VENDORS, nextVendors);
        setCached(CACHE_PREVIEWS, nextMenus);
        setUnavailable(false);
      } catch {
        if (cancelled) return;
        if (!getCached(CACHE_VENDORS)) {
          setVendors([]);
          setUnavailable(true);
        }
      } finally {
        if (!cancelled) setMenuPreviewsReady(true);
      }
    };

    void loadCatalog();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const params = { audience: "homepage" };
    if (SUPPORTED_MARKETPLACE_CITIES.includes(locationCity)) params.city = locationCity;
    void api.get("/announcements", { params })
      .then((response) => {
        if (!cancelled) setAnnouncements(Array.isArray(response.data) ? response.data : []);
      })
      .catch(() => {
        if (!cancelled) setAnnouncements([]);
      });
    return () => { cancelled = true; };
  }, [locationCity]);

  useEffect(() => {
    let cancelled = false;
    let idleHandle = null;
    let timeoutHandle = null;
    const eligible = (vendors || []).filter((vendor) => vendor?.id);
    if (!eligible.length) {
      setPublicDrops([]);
      return () => { cancelled = true; };
    }

    // Drops live below the primary marketplace discovery rail. Defer their
    // network work until the browser has rendered the critical homepage so
    // they do not compete with images, catalog data, auth, or settings.
    const loadDrops = () => {
      void Promise.all(eligible.map((vendor) => api.get(`/drops/vendor/${vendor.id}/public`)
        .then(({ data }) => (Array.isArray(data?.items) ? data.items : [])
          .filter((drop) => drop?.state === "open" || drop?.state === "scheduled")
          .map((drop) => ({ ...drop, vendor })))
        .catch(() => [])))
        .then((groups) => {
          if (!cancelled) setPublicDrops(groups.flat().slice(0, 4));
        });
    };

    if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
      idleHandle = window.requestIdleCallback(loadDrops, { timeout: 1800 });
    } else {
      timeoutHandle = window.setTimeout(loadDrops, 650);
    }

    return () => {
      cancelled = true;
      if (idleHandle !== null && typeof window.cancelIdleCallback === "function") {
        window.cancelIdleCallback(idleHandle);
      }
      if (timeoutHandle !== null) window.clearTimeout(timeoutHandle);
    };
  }, [vendors]);

  const cuisines = vendors
    ? Array.from(new Set(vendors.map((v) => v.cuisine).filter(Boolean)))
        .filter((c) => !HIDE_CUISINE_PATTERNS.some((re) => re.test(c)))
    : [];

  const foodCategories = Array.from(
    new Set(
      Object.values(menuPreviews || {})
        .flat()
        .map((item) => String(item?.category || "").trim())
        .filter(Boolean)
    )
  )
    .filter((category) => !HIDE_CUISINE_PATTERNS.some((re) => re.test(category)))
    .sort((a, b) => a.localeCompare(b));

  const featuredIds = marketplace.featured_vendor_ids || [];
  const photoEligibleVendors = vendors
    ? vendors.filter((vendor) => (menuPreviews[vendor.id] || []).some(
        (item) => item?.available !== false && String(item?.image_url || "").trim()
      ))
    : null;
  const sorted = photoEligibleVendors ? [...photoEligibleVendors].sort((a, b) => {
    if (marketplace.vendor_order === "alphabetical") {
      return String(a.business_name || "").localeCompare(String(b.business_name || ""));
    }
    if (marketplace.vendor_order === "featured_first") {
      const aIndex = featuredIds.indexOf(a.id);
      const bIndex = featuredIds.indexOf(b.id);
      const aFeatured = aIndex >= 0;
      const bFeatured = bIndex >= 0;
      if (aFeatured !== bFeatured) return aFeatured ? -1 : 1;
      if (aFeatured && bFeatured && aIndex !== bIndex) return aIndex - bIndex;
    }
    const aOpen = a.is_open !== false ? 0 : 1;
    const bOpen = b.is_open !== false ? 0 : 1;
    return aOpen - bOpen;
  }) : null;

  const locationFiltered = sorted?.filter((v) => vendorMatchesLocation(v, locationCity));

  const filtered = locationFiltered?.filter((v) => {
    if (cuisine && v.cuisine !== cuisine) return false;
    if (query && !vendorMatchesQuery(v, menuPreviews[v.id] || [], query)) return false;
    return true;
  });

  const cardLimit = Number.isInteger(marketplace.vendor_cards_per_section)
    ? Math.max(1, Math.min(100, marketplace.vendor_cards_per_section))
    : 100;
  const sections = home.sections || [];
  const sectionEnabled = (key) => sections.find((item) => item.key === key)?.enabled !== false;
  const matchingItems = useMemo(
    () => buildAvailableItems(locationFiltered || [], menuPreviews, { query, cuisine, category: foodCategory })
      .filter((item) => String(item?.image_url || "").trim())
      .filter((item) => itemMatchesMarketplaceFilters(item, {
        fulfillment: fulfillmentFilter,
        dietary: dietaryFilter,
        price: priceFilter,
      })),
    [locationFiltered, menuPreviews, query, cuisine, foodCategory, fulfillmentFilter, dietaryFilter, priceFilter]
  );
  const itemScopedFiltersActive = Boolean(foodCategory || fulfillmentFilter || dietaryFilter || priceFilter);
  const filteredKitchens = filterVendorsByMarketplaceItems(filtered || [], matchingItems, itemScopedFiltersActive);
  const hasDishFilters = Boolean(query || cuisine || locationCity || foodCategory || fulfillmentFilter || dietaryFilter || priceFilter);
  const displayed = filteredKitchens
    ? (hasDishFilters ? filteredKitchens : filteredKitchens.slice(0, Math.min(cardLimit, 8)))
    : filteredKitchens;
  const availableItems = matchingItems.slice(0, 24);
  const followedVendorIdSet = useMemo(() => new Set(followedVendorIds), [followedVendorIds]);
  const followedItems = hasDishFilters
    ? []
    : availableItems.filter((item) => followedVendorIdSet.has(item.vendor_id)).slice(0, 4);
  const topDiscoveryItems = hasDishFilters ? availableItems : availableItems.slice(0, 8);
  const featuredItems = topDiscoveryItems.slice(0, 4);
  const topSecondaryItems = topDiscoveryItems.slice(4);
  const laterDiscoveryItems = hasDishFilters ? [] : availableItems.slice(8, 16);
  const featuredItemsLoading = !menuPreviewsReady && featuredItems.length === 0;
  const showFeaturedRail = featuredItemsLoading || featuredItems.length > 0;

  const clearMarketplaceDiscovery = () => {
    setQuery("");
    setHeroSearchText("");
    setLocationCity("");
    setCuisine("");
    setFoodCategory("");
    setFulfillmentFilter("");
    setDietaryFilter("");
    setPriceFilter("");
  };

  return (
    <div className="ap-rise flex flex-col pb-4 bg-[#FAFAF8]">
      <div style={{ order: 0 }}><AbandonedOrderBanner /></div>
      {(businessStatus.emergency_notice || businessStatus.open === false) && (
        <section style={{ order: 4 }} className="border-y border-amber-300 bg-amber-50 px-4 py-3" data-testid="marketplace-operational-notice">
          <div className="mx-auto max-w-5xl text-sm font-semibold text-amber-900">
            {businessStatus.emergency_notice || businessStatus.message || "Ordering is temporarily unavailable."}
          </div>
        </section>
      )}
      {sectionEnabled("announcements") && announcements.length > 0 && (
        <section style={{ order: 5 }} className="bg-white px-4 py-3" aria-label="Marketplace announcements">
          <div className="mx-auto flex max-w-5xl flex-col gap-2 text-sm text-[#7c2d12] sm:flex-row sm:items-center sm:justify-center">
            {announcements.slice(0, 2).map((a) => <div key={a.id} className="rounded-full bg-white px-4 py-2 shadow-sm"><strong>{a.title}</strong> — {a.body}</div>)}
          </div>
        </section>
      )}
      {/* -------- EXACT OWNER-APPROVED LIGHT MARKETPLACE HERO -------- */}
      {showGuestMarketing && sectionEnabled("hero") && (
        <section
          style={{ order: 10 }}
          className="relative mx-0 mt-0 min-h-[560px] overflow-hidden rounded-none border-0 bg-[#151719] text-white sm:min-h-[620px]"
          data-testid="home-hero"
        >
          {home.hero_video_enabled === false && (
            <img src={heroBackdropImage} alt="" aria-hidden="true" loading="eager" decoding="async" fetchPriority="high" className="absolute inset-0 h-full w-full object-cover opacity-95" />
          )}
          <div className="absolute inset-0 w-full">
            <HeroFoodCarousel images={home.hero_images} fallbackImage={heroBackdropImage} videoSrc={home.hero_video_enabled !== false ? MARKETPLACE_HERO_VIDEO : ""} />
          </div>
          <div
            className="pointer-events-none absolute inset-0"
            aria-hidden="true"
            style={{
              background:
                "linear-gradient(90deg, rgba(36,79,115,.38) 0%, rgba(36,79,115,.28) 34%, rgba(36,79,115,.13) 64%, rgba(36,79,115,.04) 100%)",
            }}
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#16324a]/5 via-transparent to-[#5f82a0]/10" aria-hidden="true" />

          <div className="relative mx-auto flex min-h-[560px] w-full max-w-[1600px] items-start px-4 pb-8 pt-16 sm:min-h-[620px] sm:items-center sm:px-8 sm:py-16 lg:px-14 lg:py-20">
            <div className="w-full max-w-3xl">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-black/35 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.18em] text-white/90 backdrop-blur">
                <span>{heroBadgeText}</span>
              </div>

              <h1 className="mt-3 max-w-3xl font-serif text-[34px] font-semibold leading-[0.96] tracking-[-0.025em] text-white sm:mt-5 sm:text-6xl sm:font-black sm:tracking-[-0.045em] lg:text-[76px]">
                <span>{heroHeadline} <span className="text-[#f05b55]">{heroHeadlineEmphasis}</span></span>
              </h1>
              <p className="mt-2 max-w-2xl text-[12px] font-medium leading-5 text-white/90 sm:mt-5 sm:text-lg sm:leading-7">
                <span>{heroSubheadline}</span>
              </p>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  const value = heroSearchText.trim();
                  const parsed = parseMarketplaceSearch(value);
                  if (parsed.city) {
                    setLocationCity(parsed.city);
                    setQuery(parsed.query);
                    setCuisine("");
                    setFoodCategory("");
                  } else if (value && (/\b\d{5}(?:-\d{4})?\b/.test(value) || /\b(?:ak|alaska)\b/i.test(value))) {
                    toast.error("Arctic Plate currently serves Anchorage, Wasilla, Palmer, and Fairbanks.");
                    return;
                  } else {
                    setLocationCity("");
                    setQuery(value);
                    setCuisine("");
                    setFoodCategory("");
                  }
                  document.getElementById("available-now")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                className="mt-4 flex max-w-2xl items-center rounded-xl bg-white shadow-[0_18px_55px_-28px_rgba(0,0,0,.7)] sm:mt-7 sm:rounded-2xl"
                data-testid="hero-search-form"
              >
                <div className="pl-4 text-[#0b2340]"><Search size={19} strokeWidth={2.2} /></div>
                <input
                  data-testid="hero-search-input"
                  value={heroSearchText}
                  onChange={(e) => setHeroSearchText(e.target.value)}
                  placeholder={heroSearchPlaceholder}
                  aria-label="Search Arctic Plate food, cuisines, or kitchens"
                  className="min-w-0 flex-1 bg-transparent px-3 py-3 text-xs font-semibold text-[#0b2340] placeholder:text-[#8d939a] focus:outline-none sm:py-4 sm:text-sm"
                />
                <button
                  type="submit"
                  data-testid="hero-search-submit"
                  className="m-1 shrink-0 rounded-lg bg-[#b04600] p-2.5 text-sm font-extrabold text-white transition hover:bg-[#b04600] sm:m-1.5 sm:rounded-xl sm:px-7 sm:py-3"
                  aria-label="Find food"
                >
                  <Search size={16} className="sm:hidden" />
                  <span className="hidden sm:inline">Find Food</span>
                </button>
              </form>

              <div className="mt-2 flex max-w-2xl gap-1.5 overflow-x-auto pb-1 sm:hidden ap-no-scrollbar" aria-label="Quick food filters">
                {[
                  ["Near me", ""],
                  ["Meals", "meals"],
                  ["Baked goods", "baked goods"],
                  ["Prepared foods", "prepared foods"],
                  ["Cuisines", "cuisine"],
                ].map(([label, value]) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => {
                      if (label === "Near me") {
                        if (user?.city && SUPPORTED_MARKETPLACE_CITIES.includes(user.city)) {
                          setLocationCity(user.city);
                          document.getElementById("available-now")?.scrollIntoView({ behavior: "smooth", block: "start" });
                        } else {
                          setShowCityPicker((open) => !open);
                        }
                        return;
                      }
                      if (label === "Cuisines") {
                        document.getElementById("categories")?.scrollIntoView({ behavior: "smooth", block: "start" });
                        return;
                      }
                      const patterns = {
                        Meals: /\b(meals?|entree|main)\b/i,
                        "Baked goods": /baked|bakery|bread/i,
                        "Prepared foods": /meal prep|prepared/i,
                      };
                      const category = foodCategories.find((name) => patterns[label]?.test(name));
                      setFoodCategory(category || "");
                      setQuery(category ? "" : value);
                      document.getElementById("available-now")?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                    className="shrink-0 rounded-full border border-white/35 bg-black/30 px-3 py-1.5 text-[10px] font-bold text-white backdrop-blur"
                  >
                    {label}
                  </button>
                ))}
              </div>
              {showCityPicker && (
                <label className="mt-2 block max-w-xs text-xs font-bold text-white sm:hidden">
                  Choose your Alaska city
                  <select
                    value={locationCity}
                    onChange={(event) => {
                      setLocationCity(event.target.value);
                      setShowCityPicker(false);
                      document.getElementById("available-now")?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                    className="mt-1 min-h-11 w-full rounded-xl border border-white/40 bg-[#10233f] px-3 text-sm text-white"
                    aria-label="Choose your Alaska city"
                  >
                    <option value="">All supported cities</option>
                    {SUPPORTED_MARKETPLACE_CITIES.map((city) => <option key={city} value={city}>{city}</option>)}
                  </select>
                </label>
              )}

              <div className="mt-3 hidden max-w-2xl flex-wrap gap-2 sm:flex">
                <button
                  type="button"
                  onClick={() => {
                    setFulfillmentFilter(fulfillmentFilter === "pickup" ? "" : "pickup");
                    document.getElementById("available-now")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className={`inline-flex min-h-10 items-center gap-2 rounded-full border px-4 text-xs font-extrabold backdrop-blur ${
                    fulfillmentFilter === "pickup" ? "border-white bg-white text-[#0b2340]" : "border-white/40 bg-black/25 text-white"
                  }`}
                >
                  <Store size={14} /> Pickup
                </button>
                <button
                  type="button"
                  onClick={() => document.getElementById("weekly-drops-story")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  className="inline-flex min-h-10 items-center gap-2 rounded-full border border-white/40 bg-black/25 px-4 text-xs font-extrabold text-white backdrop-blur"
                >
                  <Package size={14} /> Weekly Drops
                </button>
              </div>
              <div className="mt-3 hidden text-xs font-medium text-white/75 sm:block" data-testid="hero-search-status">
                {locationCity
                  ? `Showing local food in ${locationCity}.`
                  : query
                    ? `Showing results for “${query}”.`
                    : "Serving Anchorage, Wasilla, Palmer, and Fairbanks."}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* -------- SIGNED-IN CUSTOMER SHOPPING HEADER -------- */}
      {user && user.role === "customer" && !previewMode && (
        <section style={{ order: 10 }} className="border-b border-[#E7E5E4] bg-[#FAFAF8] text-[#0b2340]" data-testid="customer-discovery-header">
          <div className="mx-auto max-w-[1600px] px-5 py-6 sm:px-8 lg:px-12 lg:py-8">
            <div className="text-xs font-semibold text-[#b04600]">Welcome back{user.name ? `, ${user.name.split(" ")[0]}` : ""}</div>
            <h1 className="mt-1 font-serif text-2xl font-semibold leading-tight sm:text-3xl">What are you hungry for today?</h1>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const value = heroSearchText.trim();
                const parsed = parseMarketplaceSearch(value);
                if (parsed.city) {
                  setLocationCity(parsed.city);
                  setQuery(parsed.query);
                  setCuisine("");
                  setFoodCategory("");
                } else if (value && (/\b\d{5}(?:-\d{4})?\b/.test(value) || /\b(?:ak|alaska)\b/i.test(value))) {
                  toast.error("Arctic Plate currently serves Anchorage, Wasilla, Palmer, and Fairbanks.");
                  return;
                } else {
                  setLocationCity("");
                  setQuery(value);
                  setCuisine("");
                  setFoodCategory("");
                }
                document.getElementById("available-now")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="mt-4 flex max-w-3xl items-center overflow-hidden rounded-2xl border border-[#e3d9cc] bg-white"
              data-testid="customer-discovery-search"
            >
              <div className="pl-4 text-[#0b2340]"><Search size={18} strokeWidth={2.2} /></div>
              <input
                value={heroSearchText}
                onChange={(e) => setHeroSearchText(e.target.value)}
                placeholder="Search food, cuisines, or kitchens"
                aria-label="Search Arctic Plate food, cuisines, or kitchens"
                className="min-w-0 flex-1 bg-transparent px-3 py-3.5 text-sm font-semibold text-[#0b2340] placeholder:text-[#8d939a] focus:outline-none"
              />
              <button type="submit" className="sr-only">Search</button>
            </form>
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1" aria-label="Customer food filters">
              <button
                type="button"
                onClick={() => {
                  setFulfillmentFilter("");
                  document.getElementById("available-now")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                aria-pressed={!fulfillmentFilter}
                className={`inline-flex min-h-10 shrink-0 items-center gap-2 rounded-full border px-4 text-xs font-extrabold ${!fulfillmentFilter ? "border-[#0b2340] bg-[#0b2340] text-white" : "border-[#e3d9cc] bg-white text-[#0b2340]"}`}
              >
                <Utensils size={14} /> Available now
              </button>
              <button
                type="button"
                onClick={() => {
                  setFulfillmentFilter(fulfillmentFilter === "pickup" ? "" : "pickup");
                  document.getElementById("available-now")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                aria-pressed={fulfillmentFilter === "pickup"}
                className={`inline-flex min-h-10 shrink-0 items-center gap-2 rounded-full border px-4 text-xs font-extrabold ${fulfillmentFilter === "pickup" ? "border-[#0b2340] bg-[#0b2340] text-white" : "border-[#e3d9cc] bg-white text-[#0b2340]"}`}
              >
                <Store size={14} /> Pickup
              </button>
              <button
                type="button"
                onClick={() => {
                  setFulfillmentFilter(fulfillmentFilter === "delivery" ? "" : "delivery");
                  document.getElementById("available-now")?.scrollIntoView({ behavior: "smooth", block: "start" });
                }}
                aria-pressed={fulfillmentFilter === "delivery"}
                className={`inline-flex min-h-10 shrink-0 items-center gap-2 rounded-full border px-4 text-xs font-extrabold ${fulfillmentFilter === "delivery" ? "border-[#0b2340] bg-[#0b2340] text-white" : "border-[#e3d9cc] bg-white text-[#0b2340]"}`}
              >
                <Truck size={14} /> Delivery
              </button>
              {publicDrops.length > 0 && (
                <button
                  type="button"
                  onClick={() => document.getElementById("marketplace-public-drops")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  className="inline-flex min-h-10 shrink-0 items-center gap-2 rounded-full border border-[#e3d9cc] bg-white px-4 text-xs font-extrabold text-[#0b2340]"
                >
                  <Package size={14} /> Drops
                </button>
              )}
            </div>
          </div>
        </section>
      )}

      {/* -------- EXACT APPROVED FOOD-FIRST DISCOVERY -------- */}
      <section id="available-now" style={{ order: 20 }} className="scroll-mt-24 border-y border-[#E7E5E4] bg-[#FAFAF8]" data-testid="available-now">
        <div className="mx-auto max-w-[1600px] px-5 py-9 sm:px-8 lg:px-14 lg:py-12">
          <div className="hidden items-end justify-between gap-5 sm:flex">
            <div>
              <div className="text-sm font-semibold text-[#6f472c]">Available now</div>
              <h2 className="mt-1 font-serif text-4xl font-semibold leading-none tracking-[-0.025em] text-[#0b2340] sm:text-5xl">Food available now</h2>
              <p className="mt-2 text-sm text-[var(--ap-ink-soft)] lg:text-base">Current dishes from local Arctic Plate sellers.</p>
            </div>
            {availableItems.length > 4 && (
              <button
                type="button"
                onClick={() => setShowAllFood(true)}
                className="hidden items-center gap-2 text-sm font-extrabold text-[#9a4a00] sm:inline-flex"
              >
                View All <ArrowRight size={16} />
              </button>
            )}
          </div>

          {foodCategories.length > 0 && (
            <div id="categories" className="mt-1 scroll-mt-24 sm:mt-6" data-testid="home-category-rail">
              <CategoryRail cuisines={foodCategories} active={foodCategory} onPick={(next) => setFoodCategory(next)} />
            </div>
          )}

          {showFeaturedRail && (
            <div className="mt-5 sm:hidden">
              <div className="text-xs font-semibold text-[#b04600]">Available now</div>
              <div className="mt-0.5 flex min-w-0 items-center justify-between gap-3">
                <h2 className="min-w-0 font-serif text-[28px] font-semibold leading-none text-[#0b2340]">Local food</h2>
                {availableItems.length > 4 && (
                  <button type="button" onClick={() => setShowAllFood(true)} className="shrink-0 text-[10px] font-extrabold text-[#0b2340]">View all <ArrowRight size={11} className="inline" /></button>
                )}
              </div>
            </div>
          )}

          {/* Food discovery stays horizontal on mobile. Seller menu cards use
              only the seller's actual item photo. If that image is missing or
              fails, a neutral Arctic Plate placeholder keeps the rail stable. */}
          {showFeaturedRail && (
          <div className="mt-6 grid auto-cols-[27vw] grid-flow-col gap-2 overflow-x-auto pb-2 pr-5 snap-x snap-mandatory sm:auto-cols-auto sm:grid-flow-row sm:grid-cols-2 sm:gap-3 sm:overflow-visible sm:pb-0 sm:pr-0 xl:grid-cols-4" data-testid="approved-featured-food-grid">
            {featuredItemsLoading
              ? [0, 1, 2, 3].map((index) => (
                  <div
                    key={index}
                    className="snap-start overflow-hidden rounded-2xl border border-[#e3e1dd] bg-white shadow-[0_18px_36px_-24px_rgba(19,41,75,.42)]"
                    data-testid="featured-items-loading"
                    aria-hidden="true"
                  >
                    <div className="aspect-[16/9] animate-pulse bg-[#ece9e4]" />
                    <div className="space-y-2 px-3 py-3">
                      <div className="h-4 w-2/3 animate-pulse rounded bg-[#ece9e4]" />
                      <div className="h-3 w-1/2 animate-pulse rounded bg-[#ece9e4]" />
                      <div className="h-5 w-24 animate-pulse rounded-full bg-[#eee9e2]" />
                    </div>
                  </div>
                ))
              : featuredItems.map((item, index) => (
                  <ApprovedFeaturedCard key={`${item.vendor_id}:${item.id}`} item={item} index={index} />
                ))}
          </div>
          )}

          <div className="mt-4 hidden sm:block">
            <MarketplaceFilterBar
              fulfillment={fulfillmentFilter}
              onFulfillment={setFulfillmentFilter}
              dietary={dietaryFilter}
              onDietary={setDietaryFilter}
              price={priceFilter}
              onPrice={setPriceFilter}
              onClear={clearMarketplaceDiscovery}
            />
          </div>

          {!featuredItemsLoading && matchingItems.length === 0 && (
            <div className="mt-6 rounded-[1.5rem] border border-dashed border-[#d6d3d1] bg-white px-6 py-9 text-center" data-testid="available-items-empty">
              <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-2xl bg-[#fff4ec] text-[#b04600]">
                <Search size={20} />
              </div>
              <h3 className="mt-3 font-serif text-2xl font-semibold text-[#0b2340]">
                {hasDishFilters ? "No dishes match those filters." : "No dishes are available right now."}
              </h3>
              <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-[#4e5665]">
                {hasDishFilters
                  ? "Clear the filters or browse the local businesses below."
                  : "Browse local businesses below and check back as menus open."}
              </p>
              {hasDishFilters && (
                <button
                  type="button"
                  onClick={clearMarketplaceDiscovery}
                  className="mt-4 inline-flex min-h-11 items-center justify-center rounded-full bg-[#0b2340] px-5 text-xs font-extrabold text-white"
                >
                  Clear all filters
                </button>
              )}
            </div>
          )}

          <div id="all-live-food" className="scroll-mt-24">
            {vendors === null && !unavailable ? (
              <div className="mt-5 grid auto-cols-[44vw] grid-flow-col gap-3 overflow-x-auto pb-2 pr-5 snap-x snap-mandatory sm:auto-cols-auto sm:grid-flow-row sm:grid-cols-2 sm:overflow-visible sm:pb-0 sm:pr-0 lg:grid-cols-3 xl:grid-cols-4" data-testid="available-items-loading">
                {[0,1,2,3].map((i) => (
                  <div key={i} className="overflow-hidden rounded-2xl border border-[#e3e1dd] bg-white">
                    <div className="aspect-[4/3] animate-pulse bg-[#ece9e4]" />
                    <div className="space-y-2 p-4"><div className="h-3 w-2/3 animate-pulse rounded bg-[#ece9e4]" /><div className="h-3 w-1/2 animate-pulse rounded bg-[#ece9e4]" /></div>
                  </div>
                ))}
              </div>
            ) : topSecondaryItems.length > 0 ? (
              <div className="mt-5 grid auto-cols-[44vw] grid-flow-col gap-3 overflow-x-auto pb-2 pr-5 snap-x snap-mandatory sm:auto-cols-auto sm:grid-flow-row sm:grid-cols-2 sm:overflow-visible sm:pb-0 sm:pr-0 lg:grid-cols-4" data-testid="available-item-list">
                {topSecondaryItems.map((item) => (
                  <MenuItemCard key={`${item.vendor_id}:${item.id}`} item={item} />
                ))}
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {showGuestMarketing && (
        <div style={{ order: 30 }} className="mt-8">
          <MarketplaceStorySections
            phase="early"
            hasDropSellers={Boolean(vendors?.some((vendor) => vendor?.id))}
            showBusinessPromotion={false}
          />
        </div>
      )}

      {user?.role === "customer" && !previewMode && followedItems.length > 0 && (
        <section style={{ order: 35 }} className="mx-auto w-full max-w-[1600px] px-5 py-8 sm:px-8 lg:px-12" data-testid="customer-following-food">
          <div className="flex items-end justify-between gap-4">
            <div>
              <div className="text-sm font-semibold text-[#6f472c]">Because you follow them</div>
              <h2 className="mt-1 font-serif text-3xl font-semibold text-[#0b2340] sm:text-4xl">New from kitchens you follow</h2>
            </div>
            <Link to="/following" className="shrink-0 text-xs font-bold text-[#9a4a00]">See kitchens <ArrowRight size={12} className="inline" /></Link>
          </div>
          <div className="mt-5 grid auto-cols-[44vw] grid-flow-col gap-3 overflow-x-auto pb-2 pr-5 snap-x snap-mandatory sm:auto-cols-auto sm:grid-flow-row sm:grid-cols-2 sm:overflow-visible sm:pb-0 sm:pr-0 lg:grid-cols-4">
            {followedItems.map((item) => <MenuItemCard key={`followed-${item.vendor_id}:${item.id}`} item={item} />)}
          </div>
        </section>
      )}

      {publicDrops.length > 0 && <section style={{ order: 40 }} className="bg-[#FFF8F4] px-5 py-10 sm:px-8 lg:px-14" data-testid="marketplace-public-drops">
        <div className="mx-auto max-w-[1600px]">
          <div className="text-sm font-semibold text-[#6f472c]">From approved Arctic Plate sellers</div>
          <h2 className="mt-1 font-serif text-4xl font-semibold text-[#10233f] sm:text-5xl">Upcoming drops & preorders</h2>
          <p className="mt-2 text-sm text-[var(--ap-ink-soft)]">Order during each kitchen’s published window and see pickup availability before checkout.</p>
          <div className="mt-6 grid auto-cols-[78vw] grid-flow-col gap-4 overflow-x-auto pb-2 pr-5 snap-x snap-mandatory sm:auto-cols-auto sm:grid-flow-row sm:grid-cols-2 sm:overflow-visible sm:pb-0 sm:pr-0 lg:grid-cols-4">
            {publicDrops.map((drop) => {
              const firstItem = (menuPreviews[drop.vendor.id] || []).find((item) => (drop.menu_item_ids || []).includes(item.id));
              const image = firstItem?.image_url || drop.vendor?.image_url;
              const opening = drop.state === "open" ? drop.order_closes_at : drop.order_opens_at;
              const date = opening ? new Date(opening) : null;
              return <Link key={drop.id} to={`/drop/${drop.id}`} className="group overflow-hidden rounded-2xl border border-[#ead7c9] bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                {image && <img src={image} alt="" loading="lazy" decoding="async" fetchPriority="low" className="aspect-[4/3] w-full object-cover" />}
                <div className="p-4">
                  <span className="rounded-full bg-[#b04600] px-3 py-1 text-[10px] font-bold text-white">{drop.state === "open" ? "Preorder open" : "Coming soon"}</span>
                  <h3 className="mt-3 font-serif text-xl font-semibold text-[#10233f]">{drop.title}</h3>
                  <p className="mt-1 text-xs text-[#475467]">{drop.vendor.business_name}</p>
                  {date && !Number.isNaN(date.getTime()) && <p className="mt-2 text-xs font-semibold text-[#10233f]">{drop.state === "open" ? "Closes" : "Opens"} {date.toLocaleString()}</p>}
                  <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold text-[#b04600]">View drop <ArrowRight size={13} /></span>
                </div>
              </Link>;
            })}
          </div>
        </div>
      </section>}

      {/* -------- KITCHENS SECTION -------- */}
      <div id="kitchens" style={{ order: 50 }} className="mx-auto w-full max-w-[1600px] px-5 sm:px-8 lg:px-12 pt-8 lg:pt-16">

        {/* Loading skeleton */}
        {vendors === null && !unavailable && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-7" data-testid="vendors-loading">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="bg-white border border-[#e5e7eb] rounded-3xl overflow-hidden">
                <div className="aspect-[4/3] bg-[#f3f4f6] animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-3 w-1/3 bg-[#f3f4f6] rounded animate-pulse" />
                  <div className="h-5 w-2/3 bg-[#f3f4f6] rounded animate-pulse" />
                  <div className="h-3 w-1/2 bg-[#f3f4f6] rounded animate-pulse" />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Empty state — premium launch messaging */}
        {filteredKitchens && filteredKitchens.length === 0 && (
          <EmptyState
            isTruEmpty={vendors && (vendors.length === 0 || unavailable)}
            onClear={clearMarketplaceDiscovery}
          />
        )}

        {filteredKitchens && filteredKitchens.length > 0 && (
          showGuestMarketing ? (
            <section
              className="mb-8 overflow-hidden rounded-[2rem] border border-[#3a3024] bg-[#0b0d0f] text-white shadow-[0_30px_70px_-38px_rgba(0,0,0,.72)] lg:mb-10"
              style={{ backgroundImage: `linear-gradient(90deg, rgba(8,10,12,.90), rgba(8,10,12,.58) 55%, rgba(8,10,12,.24)), url('${heroBackdropImage}')`, backgroundSize: "cover", backgroundPosition: "center" }}
              data-testid="approved-local-kitchens-banner"
            >
              <div className="grid gap-6 px-6 py-8 lg:grid-cols-[1.25fr_1fr] lg:items-center lg:px-10">
                <div>
                  <div className="text-sm font-semibold text-[#e2a176]">Local sellers</div>
                  <h2 className="mt-1 font-serif text-4xl font-semibold leading-none text-white">Browse local kitchens</h2>
                  <p className="mt-3 max-w-xl text-sm leading-6 text-white/82">
                    See current storefronts, menus, pickup options, and delivery where available.
                  </p>
                  <button
                    type="button"
                    onClick={() => document.getElementById("vendor-list")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                    className="mt-5 inline-flex items-center gap-2 rounded-full bg-[#C25A2E] px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-[#a94b25]"
                  >
                    Browse kitchens <ArrowRight size={15} />
                  </button>
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <CommunityPoint inverse icon={<Store size={22} />} label="Local Food Businesses" />
                  <CommunityPoint inverse icon={<Sparkles size={22} />} label="Diverse Cuisines" />
                  <CommunityPoint inverse icon={<ShieldCheck size={22} />} label="Independent Alaska Food" />
                </div>
              </div>
            </section>
          ) : (
            <div className="mb-5" data-testid="customer-local-kitchens-heading">
              <div className="text-sm font-semibold text-[#6f472c]">Local sellers</div>
              <h2 className="mt-1 font-serif text-3xl font-semibold text-[#0b2340] sm:text-4xl">Browse local kitchens</h2>
              <p className="mt-2 text-sm text-[var(--ap-ink-soft)]">Open a storefront to see its current menu, pickup, and available delivery options.</p>
            </div>
          )
        )}

        {/* Kitchen grid — premium editorial cards */}
        <div id="vendor-list" className="grid auto-cols-[84vw] grid-flow-col gap-5 overflow-x-auto pb-2 pr-5 snap-x snap-mandatory sm:auto-cols-auto sm:grid-flow-row sm:grid-cols-2 sm:overflow-visible sm:pb-0 sm:pr-0 lg:grid-cols-4 lg:gap-6" data-testid="vendor-list">
          {displayed?.map((v, i) => (
            <VendorCard
              key={v.id}
              vendor={v}
              preview={(menuPreviews[v.id] || []).slice(0, 3)}
              index={i}
              promoBadge={(marketplace.promotional_badges || {})[v.id] || ""}
            />
          ))}
        </div>
      </div>

      {laterDiscoveryItems.length > 0 && (
        <section style={{ order: 60 }} className="mx-auto w-full max-w-[1600px] px-5 py-10 sm:px-8 lg:px-12 lg:py-14" data-testid="marketplace-second-discovery">
          <div className="flex items-end justify-between gap-4">
            <div>
              <div className="text-sm font-semibold text-[#6f472c]">Keep exploring</div>
              <h2 className="mt-1 font-serif text-3xl font-semibold text-[#0b2340] sm:text-4xl">More local food to discover</h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-[var(--ap-ink-soft)]">More dishes currently listed by local Arctic Plate sellers.</p>
            </div>
            <button type="button" onClick={() => document.getElementById("available-now")?.scrollIntoView({ behavior: "smooth", block: "start" })} className="hidden rounded-full border border-[#d6d3d1] bg-white px-4 py-2 text-xs font-bold text-[#13294b] sm:inline-flex">Back to filters</button>
          </div>
          <div className="mt-6 grid auto-cols-[78vw] grid-flow-col gap-5 overflow-x-auto pb-2 pr-5 snap-x snap-mandatory sm:auto-cols-auto sm:grid-flow-row sm:grid-cols-2 sm:overflow-visible sm:pb-0 sm:pr-0 lg:grid-cols-4">
            {laterDiscoveryItems.map((item) => (
              <MenuItemCard key={`later-${item.vendor_id}:${item.id}`} item={item} />
            ))}
          </div>
        </section>
      )}

      {showAllFood && (
        <section id="full-food-catalog" style={{ order: 65 }} className="mx-auto w-full max-w-[1600px] scroll-mt-24 px-5 py-10 sm:px-8 lg:px-12" data-testid="full-food-catalog">
          <div className="flex items-center justify-between gap-3">
            <h2 className="font-serif text-3xl font-semibold text-[#0b2340]">All available food</h2>
            <button type="button" onClick={() => setShowAllFood(false)} className="shrink-0 rounded-full border border-[#d6d3d1] bg-white px-4 py-2 text-xs font-bold text-[#13294b]">Show less</button>
          </div>
          <p className="mt-2 text-sm text-[var(--ap-ink-soft)]">{matchingItems.length} dish{matchingItems.length === 1 ? "" : "es"} match your current selections.</p>
          <div className="mt-5 grid auto-cols-[78vw] grid-flow-col gap-3 overflow-x-auto pb-3 pr-5 snap-x snap-mandatory sm:auto-cols-auto sm:grid-flow-row sm:grid-cols-2 sm:overflow-visible sm:pb-0 sm:pr-0 lg:grid-cols-4">
            {matchingItems.map((item) => <div key={`all-${item.vendor_id}:${item.id}`} className="min-w-0 snap-start"><MenuItemCard item={item} /></div>)}
          </div>
        </section>
      )}

      {showGuestMarketing && (
        <div style={{ order: 70 }}>
          <MarketplaceStorySections
            phase="late"
            hasDropSellers={Boolean(vendors?.some((vendor) => vendor?.id))}
            showBusinessPromotion={false}
          />
        </div>
      )}

    </div>
  );
}

/* ---------------- Menu item discovery card ---------------- */
export function MenuItemCard({ item }) {
  const unavailable = item.vendor_is_open === false;
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [item.image_url]);

  return (
    <Link
      to={`/vendor/${item.vendor_id}/product/${item.id}`}
      data-testid={`available-item-${item.vendor_id}-${item.id}`}
      className="group snap-start overflow-hidden rounded-2xl border border-[var(--ap-border)] bg-white shadow-sm transition-colors duration-200 hover:border-[#c55a11]/55"
    >
      <div className="relative aspect-[16/10] overflow-hidden bg-[#171a1d] sm:aspect-[5/4]">
        <div
          className="absolute inset-0 flex h-full w-full items-center justify-center bg-[#24272a] text-[#d98552]"
          data-testid={`menu-image-placeholder-${item.vendor_id}-${item.id}`}
          aria-label="Photo coming soon"
        >
          <Utensils size={24} strokeWidth={1.5} />
          <span className="mt-2 text-[9px] font-bold uppercase tracking-[0.16em] text-white/55">Photo coming soon</span>
        </div>
        {String(item.image_url || "").trim() && !imageFailed && (
          <img
            src={item.image_url}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
            data-testid={`menu-primary-image-${item.vendor_id}-${item.id}`}
            onError={() => setImageFailed(true)}
          />
        )}
        <div className="absolute left-2 top-2 flex gap-1.5">
          {unavailable ? (
            <span className="rounded-full bg-[#1f2421]/90 px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-white">Kitchen closed</span>
          ) : (
            <span className="rounded-full border border-[#b7d3c3] bg-white/95 px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-[#0f6b3a] shadow-sm">Available</span>
          )}
        </div>
      </div>
      <div className="p-3 sm:p-5">
        <div className="flex items-start justify-between gap-2">
          <h3 className="min-w-0 font-serif text-base font-semibold leading-[1.08] tracking-[-0.01em] text-[#13294b] group-hover:text-[#b44c07] sm:text-[22px]">{item.name}</h3>
          <div className="shrink-0 rounded-full bg-[#f2dcc9] px-2 py-1 text-xs font-bold tabular-nums text-[#a84608] sm:px-2.5 sm:text-sm">${item.price.toFixed(2)}</div>
        </div>
        <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs font-semibold text-[var(--ap-ink-soft)]">
          <span className="truncate">{item.vendor_name}</span>
          {item.vendor_review_count > 0 && (
            <span className="inline-flex shrink-0 items-center gap-1 text-[#35404b]">
              <Star size={11} className="fill-[#f39c12] text-[#f39c12]" />
              {item.vendor_rating.toFixed(1)}
              <span className="font-normal text-[#667085]">({item.vendor_review_count})</span>
            </span>
          )}
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {item.accepts_pickup && <span className="inline-flex items-center gap-1 rounded-full bg-[#f3f4f6] px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-[#4b5563]"><Store size={9} /> Pickup</span>}
          {item.accepts_delivery && <span className="inline-flex items-center gap-1 rounded-full bg-[#fff4ec] px-2 py-1 text-[9px] font-bold uppercase tracking-wider text-[#b04600]"><Truck size={9} /> Delivery</span>}
        </div>
      </div>
    </Link>
  );
}

/* ---------------- Vendor card ---------------- */
function VendorCard({ vendor: v, preview, index, promoBadge }) {
  const isClosed = v.is_open === false;
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [v.image_url]);
  return (
    <article
      className={`group relative overflow-hidden rounded-2xl border border-[#2f2923] bg-[#0d0f11] text-white ap-rise shadow-sm transition-colors duration-200 hover:border-[#C25A2E]/70 ${
        isClosed ? "opacity-70 grayscale" : ""
      }`}
      style={{ animationDelay: `${Math.min(index, 8) * 60}ms` }}
      data-testid={`vendor-card-${v.id}`}
    >
      <Link
        to={`/vendor/${v.id}`}
        aria-label={isClosed ? `${v.business_name} (closed — menu viewable)` : v.business_name}
        className="block"
      >
      <div className={v.image_url && !imageFailed ? "relative aspect-[4/3] overflow-hidden" : "relative h-28 overflow-hidden sm:h-32"}>
        {v.image_url && !imageFailed ? (
          <img
            src={v.image_url}
            alt={`${v.business_name || "Local kitchen"} storefront`}
            loading="lazy"
            decoding="async"
            fetchPriority="low"
            className="absolute inset-0 h-full w-full object-cover bg-[#f3f4f6] group-hover:scale-[1.06] transition-transform duration-700"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div
            className="absolute inset-0 flex items-center justify-center bg-[#202326] text-[#d98552]"
            data-testid={`vendor-image-placeholder-${v.id}`}
          >
            <Store size={28} strokeWidth={1.5} />
          </div>
        )}
        {/* Bottom gradient for legibility of badges */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />

        {/* Corner badges */}
        <div className="absolute top-3 left-3 right-3 flex items-start justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {isClosed ? (
              <span
                data-testid={`vendor-closed-badge-${v.id}`}
                className="inline-flex items-center px-2.5 py-1 rounded-full bg-[#1f2421] text-white text-[9px] tracking-[0.2em] uppercase font-bold"
              >
                Closed
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[#0f6b3a] border border-[#bfe5cc] text-[9px] tracking-[0.2em] uppercase font-bold">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0f6b3a]" /> Open
              </span>
            )}
            {promoBadge && (
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[var(--ap-primary)] border border-[#e5e7eb] text-[9px] tracking-[0.2em] uppercase font-bold">
                {promoBadge}
              </span>
            )}
          </div>
          {v.review_count > 0 && (
            <span
              data-testid={`vendor-rating-${v.id}`}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/95 backdrop-blur text-[#1f2421] text-[11px] font-bold shadow-sm"
            >
              <Star size={11} className="fill-[#f39c12] text-[#f39c12]" />
              {Number(v.avg_rating).toFixed(1)}
              <span className="text-[#4b5563] font-normal">({v.review_count})</span>
            </span>
          )}
        </div>
      </div>

      <div className="p-5 lg:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            {v.cuisine && (
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#b04600] font-bold">{v.cuisine}</div>
            )}
            <h3
              className="mt-0.5 truncate font-serif text-xl font-semibold leading-tight text-white transition-colors group-hover:text-[#e89a6b] lg:text-2xl"
              data-testid={`vendor-name-${v.id}`}
            >
              {v.business_name || "Unnamed Kitchen"}
            </h3>
          </div>
        </div>
        <div className="mt-1.5 flex items-center gap-1.5 text-sm text-white/72">
          <MapPin size={12} /> <span className="truncate">{v.pickup_area || publicPickupArea(v.pickup_address)}</span>
        </div>

        <div className="mt-3 flex items-center gap-1.5 flex-wrap" data-testid={`vendor-services-${v.id}`}>
          {v.accepts_pickup !== false && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#f3f4f6] text-[#1f2421] text-[10px] tracking-[0.12em] uppercase font-bold">
              <Store size={10} strokeWidth={2.2} /> Pickup
            </span>
          )}
          {Boolean(v.accepts_vendor_delivery || (v.accepts_arctic_plate_delivery !== false && v.arctic_plate_delivery_admin_approved)) && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#fff4ec] text-[#b04600] text-[10px] tracking-[0.12em] uppercase font-bold">
              <Truck size={10} strokeWidth={2.2} /> Delivery
            </span>
          )}
        </div>

        <div className="mt-4 border-t border-white/12 pt-4">
          <div className="text-sm font-semibold text-white/75">Today&apos;s menu</div>
          {preview.length > 0 ? (
            <ul className="mt-2 space-y-1.5" data-testid={`menu-preview-${v.id}`}>
              {preview.map((item) => (
                <li key={item.id} className="flex items-center justify-between gap-2 truncate text-sm text-white/86">
                  <span className="truncate">· {item.name}</span>
                  {typeof item.price === "number" && (
                    <span className="shrink-0 font-semibold tabular-nums text-white/58">${item.price.toFixed(2)}</span>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <div className="mt-2 text-sm italic text-white/55">Menu updates soon.</div>
          )}
        </div>

        <div
          className="mt-5 w-full rounded-full border border-[#C25A2E]/65 bg-[#C25A2E] py-3 text-center text-xs font-semibold uppercase tracking-[0.16em] text-white transition-colors group-hover:bg-[#a94b25]"
          data-testid={`view-meals-${v.id}`}
        >
          {isClosed ? "View menu" : "View meals →"}
        </div>
      </div>
      </Link>
      <div className="border-t border-white/12 px-5 py-4 lg:px-6">
        <FollowKitchenButton vendorId={v.id} vendorName={v.business_name || "this business"} compact />
      </div>
    </article>
  );
}

/* ---------------- Category rail ---------------- */
export function CategoryRail({ cuisines, active, onPick }) {
  return (
    <div className="flex gap-3 overflow-x-auto pb-2 ap-no-scrollbar sm:gap-5">
      <button
        type="button"
        onClick={() => onPick("")}
        data-testid="home-category-all"
        aria-pressed={!active}
        className={`group flex w-16 shrink-0 flex-col items-center gap-1.5 text-[10px] font-bold transition-all sm:w-20 sm:gap-2 sm:text-xs ${
          !active
            ? "text-[#b04600]"
            : "text-[#4b5563] hover:text-[#b04600]"
        }`}
      >
        <span className={`flex h-12 w-12 items-center justify-center rounded-full border-2 bg-white shadow-sm sm:h-20 sm:w-20 ${!active ? "border-[#d35400]" : "border-[#e7ded5] group-hover:border-[#d35400]"}`}><Utensils size={23} strokeWidth={1.8} /></span>
        All food
      </button>
      {cuisines.map((c) => {
        const isActive = active === c;
        return (
          <button
            key={c}
            type="button"
            onClick={() => onPick(isActive ? "" : c)}
            data-testid={`home-category-${c.toLowerCase().replace(/\s+/g, "-")}`}
            aria-pressed={isActive}
            className={`group flex w-16 shrink-0 flex-col items-center gap-1.5 text-[10px] font-bold capitalize transition-all sm:w-20 sm:gap-2 sm:text-xs ${
              isActive
                ? "text-[#b04600]"
                : "text-[#4b5563] hover:text-[#b04600]"
            }`}
          >
            <span className={`flex h-12 w-12 items-center justify-center overflow-hidden rounded-full border-2 bg-white shadow-sm sm:h-20 sm:w-20 ${isActive ? "border-[#d35400]" : "border-[#e7ded5] group-hover:border-[#d35400]"}`}>
              <Utensils size={23} strokeWidth={1.8} />
            </span>
            {c}
          </button>
        );
      })}
    </div>
  );
}

/* ---------------- Empty state ---------------- */
function EmptyState({ isTruEmpty, onClear }) {
  return (
    <div
      className="border border-[#ddd2c5] bg-white rounded-2xl px-8 py-14 lg:px-16 lg:py-20 text-center"
      data-testid="vendors-empty"
    >
      <div className="max-w-2xl mx-auto">
        <Store size={30} strokeWidth={1.6} className="mx-auto text-[#b04600]" />
        <h2 className="font-serif text-3xl sm:text-4xl font-semibold leading-tight mt-4">
          {isTruEmpty ? "More local kitchens are coming." : "No matches found"}
        </h2>
        <p className="mt-4 text-sm lg:text-base text-[var(--ap-ink-soft)] leading-relaxed">
          {isTruEmpty
            ? "We're welcoming independent local kitchens — bakers, cooks, meal-preppers, caterers — one kitchen at a time. Check back regularly for fresh local options, or share Arctic Plate with an Alaska food maker you love."
            : "Clear your search or explore other categories above."}
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          {isTruEmpty ? (
            <Link
              to="/sell-food"
              className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-[#b04600] hover:bg-[#b04600] text-white font-semibold text-xs tracking-wide uppercase transition-colors"
            >
              Sell on Arctic Plate <ArrowRight size={13} />
            </Link>
          ) : (
            <button
              onClick={onClear}
              className="inline-flex items-center gap-1.5 px-5 py-3 rounded-full bg-[#1f2421] hover:bg-black text-white font-semibold text-xs tracking-wide uppercase transition-colors"
            >
              Clear filters
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- Approved homepage primitives ---------------- */
export function ApprovedFeaturedCard({ item, index = 0 }) {
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => {
    setImageFailed(false);
  }, [item.image_url]);

  return (
    <Link
      to={`/vendor/${item.vendor_id}/product/${item.id}`}
      data-testid={`featured-item-${item.vendor_id}-${item.id}`}
      className="group min-w-0 snap-start overflow-hidden rounded-xl border border-[#e3ddd5] bg-white shadow-[0_18px_36px_-28px_rgba(19,41,75,.28)] sm:rounded-2xl"
    >
      <div className="relative aspect-[16/9] overflow-hidden bg-[#171a1d]">
        <div
          className="absolute inset-0 flex h-full w-full flex-col items-center justify-center bg-gradient-to-br from-[#171a1d] via-[#23201c] to-[#3a2c22] text-[#e89a6b]"
          data-testid={`featured-image-placeholder-${item.vendor_id}-${item.id}`}
          aria-label="Photo coming soon"
        >
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-white/15 bg-white/10 shadow-sm">
            <Utensils size={22} strokeWidth={1.5} />
          </div>
          <span className="mt-2 text-[9px] font-bold uppercase tracking-[0.16em] text-white/55">Photo coming soon</span>
        </div>
        {String(item.image_url || "").trim() && !imageFailed && (
          <img
            src={item.image_url}
            alt={item.name}
            loading={index === 0 ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={index === 0 ? "auto" : "low"}
            className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
            data-testid={`featured-primary-image-${item.vendor_id}-${item.id}`}
            onError={() => setImageFailed(true)}
          />
        )}
      </div>
      <div className="min-w-0 px-2 py-2 sm:px-3 sm:py-3">
        <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-start sm:justify-between sm:gap-3">
          <h3 className="min-w-0 line-clamp-2 text-[11px] font-extrabold leading-tight text-[#0b2340] sm:truncate sm:text-sm">{item.name}</h3>
          <span className="shrink-0 text-[11px] font-extrabold text-[#a84608] sm:text-sm">${item.price.toFixed(2)}</span>
        </div>
        <div className="mt-1 flex min-w-0 items-center gap-1 text-[9px] text-[#4e5665] sm:gap-2 sm:text-xs">
          <span className="truncate">{item.vendor_name}</span>
          {item.vendor_review_count > 0 && (
            <span className="inline-flex shrink-0 items-center gap-1 font-bold text-[#35404b]">
              <Star size={11} className="fill-[#f39c12] text-[#f39c12]" />
              {item.vendor_rating.toFixed(1)}
            </span>
          )}
        </div>
        <div className="mt-1.5 flex flex-wrap gap-1 sm:mt-2 sm:gap-1.5">
          {item.accepts_pickup && <span className="rounded-full border border-[#d5c8b8] bg-[#ece3d8] px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-[#374151] sm:px-2 sm:py-1 sm:text-[9px]">Pickup</span>}
          {item.accepts_delivery && <span className="rounded-full border border-[#e0bb84] bg-[#f4dfc1] px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wide text-[#8c5400] sm:px-2 sm:py-1 sm:text-[9px]">Delivery</span>}
        </div>
      </div>
    </Link>
  );
}

function HeroTrust({ icon, title, body }) {
  return (
    <div className="flex min-h-[84px] items-center gap-3 bg-[#fffdf8] px-4 py-4 lg:px-6">
      <div className="flex h-11 w-11 shrink-0 items-center justify-center text-[#0b2340]">{icon}</div>
      <div>
        <div className="text-sm font-extrabold leading-tight text-[#0b2340]">{title}</div>
        <div className="mt-0.5 text-[11px] leading-4 text-[#4e5665]">{body}</div>
      </div>
    </div>
  );
}

function CommunityPoint({ icon, label, inverse = false }) {
  return (
    <div className={`flex flex-col items-center rounded-2xl border px-2 py-3 text-center ${inverse ? "border-white/14 bg-black/24 text-white backdrop-blur-sm" : "border-transparent text-[#0b2340]"}`}>
      <div className={`flex h-10 w-10 items-center justify-center ${inverse ? "text-[#e89a6b]" : ""}`}>{icon}</div>
      <div className={`mt-1 text-[10px] font-bold leading-4 sm:text-[11px] ${inverse ? "text-white/86" : ""}`}>{label}</div>
    </div>
  );
}

